// =========================================================
// Vojjin COLLECT Utilities
// =========================================================
import {registerGlobal} from "./core.js";

class Collect {

    /**
     * Retrieves the 'id' or 'name' attribute of a given element within a form context.
     *
     * @param {Element|string} el - The element or selector to extract the attribute from.
     * @param {Element|Document} formElement - The form context or container to search within.
     * @returns {string|null} The value of the 'id' attribute if present, otherwise the 'name' attribute.
     *                        Returns null if neither attribute is found.
     */
    static #idOrName = (el, formElement) => {
        return $$(el, formElement).attr('id') || $$(el, formElement).attr('name');
    }

    /**
     * Retrieves the trimmed string value of the specified element.
     *
     * @param {string} selector - The selector used to locate the element.
     * @returns {string} The trimmed value of the element if it exists and has a defined value; otherwise, an empty string.
     */
    static get = (selector) => {
        let el = $$(selector);
        if (el.defined()) {
            let val = el.val();
            try {
                return val.toString().trim();
            } catch (e) {
                return "";
            }
        } else {
            return "";
        }
    }

    /**
     * Retrieves data from a specified DOM element using a data attribute.
     *
     * This function selects an element based on the provided CSS selector and checks
     * if the element is defined. If the element exists, it attempts to extract
     * the data associated with the provided data attribute selector and returns
     * the trimmed string representation of that data. If the element is not found
     * or an error occurs during the data extraction, an empty string is returned.
     *
     * @param {string} selector - The CSS selector used to identify the desired DOM element.
     * @param {string} dataSelector - The data attribute key used to retrieve the associated value.
     * @returns {string} The trimmed string value of the data attribute, or an empty string if not found or undefined.
     */
    static getFromData = (selector, dataSelector) => {
        let el = $$(selector);
        if (el.defined()) {
            let val = el.data(dataSelector);
            try {
                return val.toString().trim();
            } catch (e) {
                return "";
            }
        } else {
            return "";
        }
    }

    /**
     * Retrieves an integer value from the given selector.
     *
     * This function attempts to fetch the value associated with the provided selector
     * and converts it to an integer. If the value is not a valid integer, it returns 0.
     *
     * @param {string} selector - The identifier used to fetch the associated value.
     * @returns {number} The integer value associated with the selector, or 0 if the value is not a valid integer.
     */
    static getInt = (selector) => {
        let val = this.get(selector);
        if (!isNaN(parseInt(val))) {
            return parseInt(val);
        } else {
            return 0;
        }
    }

    static getIntFromData = (selector, dataSelector) => {
        let val = this.getFromData(selector, dataSelector);
        if (!isNaN(parseInt(val))) {
            return parseInt(val);
        } else {
            return 0;
        }
    }

    /**
     * Retrieves a floating-point number from a specified selector.
     *
     * This function attempts to retrieve a value associated with the provided
     * selector and converts it to a floating-point number. If the value cannot
     * be converted to a valid floating-point number, it defaults to returning 0.
     *
     * @param {string} selector - The selector used to retrieve the value.
     * @returns {number} The floating-point number retrieved from the selector,
     * or 0 if the value is not a valid number.
     */
    static getFloat = (selector) => {
        let val = this.get(selector);
        if (!isNaN(parseFloat(val))) {
            return parseFloat(val);
        } else {
            return 0;
        }
    }

    static getFloatFromData = (selector, dataSelector) => {
        let val = this.getFromData(selector, dataSelector);
        if (!isNaN(parseFloat(val))) {
            return parseFloat(val);
        } else {
            return 0;
        }
    }

    /**
     * Retrieves the checked state of a checkbox element identified by the given selector.
     *
     * This function checks if the selected element is defined and of type "checkbox".
     * If it is a checkbox, the function returns its checked state as a boolean.
     * If the element is either undefined or not of type "checkbox", the function returns `false`.
     *
     * @param {string} selector - The selector used to identify the target element.
     * @returns {boolean} - The checked state of the checkbox if valid, otherwise `false`.
     */
    static getChecked = (selector) => {
        let el = $$(selector);
        if (!el.defined()) return false;
        if (el.type() === "checkbox") {
            return !!el.checked();
        } else {
            return false;
        }
    }

    /**
     * Collects and returns the values of form elements within a specified selector.
     *
     * This function traverses various form input fields such as text inputs, hidden inputs,
     * textareas, checkboxes, radio buttons, email inputs, password inputs, and select dropdowns
     * to retrieve their values and structure them in an object.
     *
     * The returned object maps input element names or IDs to their respective values. For checkboxes,
     * the value is `1` if checked and `0` otherwise. For radio buttons, only the selected one will have
     * its value retrieved.
     *
     * If the selector does not resolve to a valid form element, an empty object is returned.
     *
     * @param {string} selector - The CSS selector used to resolve the form element.
     * @returns {Object} An object containing form field names or IDs as keys and their corresponding values.
     */
    static getFormValues = (selector) => {
        let formElement = $$(selector);
        if (!formElement.defined()) return {};
        let formEl = formElement.element;

        let savedata = {};

        $$("input[type=hidden]", formEl).each((el) => savedata[this.#idOrName(el, formEl)] = this.get(el))
        let counter = 0;
        $$(".fordataread", formEl).each((el) => {
            let datas = el.data("data").split(",");
            for (let i = 0; i < datas.length; i++) {
                savedata[datas[i] + "_" + counter] = el.data(datas[i]);
            }
            counter++;
        });
        $$("textarea", formEl).each((el) => {
            if (Collect.getIntFromData(el, "ckeditor") === 1) {
                let editor = CKEDITOR.instances[el.getAttribute("id")];
                savedata[this.#idOrName(el, formEl)] = editor.getData();
            } else {
                savedata[this.#idOrName(el, formEl)] = this.get(el);
            }
        })
        $$("input[type=text]", formEl).each((el) => savedata[this.#idOrName(el, formEl)] = this.get(el))
        $$("input[type=email]", formEl).each((el) => savedata[this.#idOrName(el, formEl)] = this.get(el))
        $$("input[type=password]", formEl).each((el) => savedata[this.#idOrName(el, formEl)] = this.get(el))
        $$("input[type=checkbox]", formEl).each((el) => savedata[this.#idOrName(el, formEl)] = this.getChecked(el) ? 1 : 0)
        $$("select", formEl).each((el) => savedata[this.#idOrName(el, formEl)] = this.get(el))
        $$(".taticitems", formEl).each((el) => savedata[this.#idOrName(el, formEl)] = this.get(el))
        $$("input[type=radio]", formEl).each((el) => {
            const name = this.#idOrName(el, formEl);
            if (!name) return;
            if (savedata[name] !== undefined) return;
            const checked = formEl.querySelector(`input[type=radio][name="${name}"]:checked`);
            savedata[name] = checked ? this.get(checked) : "";
        });
        return savedata;
    }

    static getTextFromSelect = (selector) => {
        return $$(selector + " option:selected").text();
    }

    static getDataFromSelect(selector, data) {
        return $$(selector + " option:selected").data(data);
    }

}

registerGlobal("Collect", Collect);
